import React, { useCallback, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import useReveal from "../../hooks/useReveal";
import SkillsetVisual from "./SkillsetVisual";
import "./SkillsetSection.css";
import "swiper/css";
import "swiper/css/pagination";

const AUTO_ADVANCE_MS = 8000;

const SkillsetSection = ({ skillsets, title, subtitle }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const sectionRef = useReveal();

  const goTo = useCallback(
    (index) => setCurrentIndex((index + skillsets.length) % skillsets.length),
    [skillsets.length]
  );

  useEffect(() => {
    if (isPaused) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const id = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % skillsets.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [isPaused, skillsets.length]);

  return (
    <section ref={sectionRef} className="skillset-section reveal">
      {(title || subtitle) && (
        <div className="skillset-header">
          {title && <h2>{title}</h2>}
          {subtitle && <p>{subtitle}</p>}
        </div>
      )}

      {/* Desktop: crossfading stage */}
      <div
        className="skillset-stage-wrapper skillset-desktop"
        role="region"
        aria-roledescription="Karussell"
        aria-label={title || "Kompetenzen"}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocus={() => setIsPaused(true)}
        onBlur={() => setIsPaused(false)}
      >
        <div className="skillset-stage">
          {skillsets.map((skillset, index) => (
            <div
              key={skillset.id}
              className={`skillset-slide ${index === currentIndex ? "active" : ""}`}
              aria-hidden={index !== currentIndex}
            >
              <div className="skillset-text">
                <span className="skillset-count">
                  {String(index + 1).padStart(2, "0")} / {String(skillsets.length).padStart(2, "0")}
                </span>
                <h3>{skillset.title}</h3>
                <p>{skillset.description}</p>
              </div>
              <div className="skillset-visual">
                <SkillsetVisual visual={skillset.visual} />
              </div>
            </div>
          ))}
        </div>

        <div className="skillset-controls">
          <button
            type="button"
            className="skillset-arrow"
            aria-label="Vorherige Kompetenz"
            onClick={() => goTo(currentIndex - 1)}
          >
            <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
              <path d="M12.79 4.21a.75.75 0 010 1.06L8.06 10l4.73 4.73a.75.75 0 11-1.06 1.06l-5.26-5.26a.75.75 0 010-1.06l5.26-5.26a.75.75 0 011.06 0z" />
            </svg>
          </button>
          <div className="skillset-indicators">
            {skillsets.map((skillset, index) => (
              <button
                key={skillset.id}
                type="button"
                className={`skillset-indicator ${index === currentIndex ? "active" : ""}`}
                aria-label={`Kompetenz ${index + 1} anzeigen: ${skillset.title}`}
                aria-current={index === currentIndex}
                onClick={() => goTo(index)}
              />
            ))}
          </div>
          <button
            type="button"
            className="skillset-arrow"
            aria-label="Nächste Kompetenz"
            onClick={() => goTo(currentIndex + 1)}
          >
            <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
              <path d="M7.21 4.21a.75.75 0 011.06 0l5.26 5.26a.75.75 0 010 1.06l-5.26 5.26a.75.75 0 11-1.06-1.06L11.94 10 7.21 5.27a.75.75 0 010-1.06z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile: swipeable cards */}
      <div className="skillset-mobile">
        <Swiper
          modules={[Pagination]}
          pagination={{ clickable: true }}
          className="skillset-swiper"
        >
          {skillsets.map((skillset) => (
            <SwiperSlide key={skillset.id}>
              <div className="skillset-mobile-slide">
                <div className="skillset-visual">
                  <SkillsetVisual visual={skillset.visual} />
                </div>
                <div className="skillset-text">
                  <h3>{skillset.title}</h3>
                  <p>{skillset.description}</p>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default SkillsetSection;